import { StateCreator } from "zustand";
import { Meeting as ApiMeeting, MeetingResource } from "../api/meetings";
import { InstanceMetadata, RecurringMetadata } from "../model/meeting_metadata";
import { Meeting, fromApiMeeting } from "../model/meeting";
import { Host, fromApiHost, fromPartnerHost } from "../model/host";
import { HostResource } from "../api/hosts";
import { PartnerResource } from "../api/partners";
import { toMeetingHostIdSchedule } from "../model/meeting_host_schedule";

// A series scope reaches the occurrences the backend rewrites: those ending
// within its look-back or later. Older ones keep whatever they had.
const SERIES_SCOPE_LOOKBACK_MS = 2 * 24 * 60 * 60 * 1000;

// The most recent loadMeetings call. Loads can overlap (a window change, Sync
// Now) and finish out of order; only the latest one may write its window.
let latestLoadId = 0;

/**
 * The staff of every partner a loaded meeting is scoped to, by id (ENG-1958):
 * they host its private sessions without being in the Hosts group. A partner
 * whose staff fail to load is skipped, so its hosts stay "Unknown host".
 */
const loadStaffHosts = async (
  apiMeetings: ApiMeeting[],
): Promise<Record<string, Host>> => {
  const partnerIds = new Set<string>();
  for (const m of apiMeetings) {
    for (const id of [
      m.metadata.partnerId,
      m.recurringMeeting?.metadata.partnerId,
    ]) {
      if (id?.trim()) partnerIds.add(id.trim());
    }
  }
  if (partnerIds.size === 0) return {};
  const [partners, ...staffByPartner] = await Promise.all([
    // The partner's name labels its staff; they still resolve without it.
    PartnerResource.getListCached().catch(() => []),
    ...Array.from(partnerIds).map((id) =>
      PartnerResource.listHosts(id)
        .then((hosts) => ({ id, hosts }))
        .catch(() => ({ id, hosts: [] })),
    ),
  ]);
  const staffHostsById: Record<string, Host> = {};
  for (const { id, hosts } of staffByPartner) {
    const partnerName = partners.find((p) => p.id === id)?.displayName ?? id;
    for (const host of hosts) {
      staffHostsById[host.appUserId] ??= fromPartnerHost(host, partnerName);
    }
  }
  return staffHostsById;
};

export interface MeetingsSlice {
  meetings: Meeting[];
  /** The Hosts group: who any meeting's host picker offers. */
  hostsById: Record<string, Host>;
  /** Staff of the partners the loaded meetings are scoped to. */
  staffHostsById: Record<string, Host>;
  start: Date;
  end: Date;
  isLoading: boolean;
  loadMeetings: (start: Date, end: Date) => void;
  setInstanceMetadata: (meetingId: string, metadata: InstanceMetadata) => void;
  setRecurringMetadata: (
    meetingId: string,
    metadata: RecurringMetadata
  ) => void;
}

export const createMeetingsSlice: StateCreator<
  MeetingsSlice,
  [],
  [],
  MeetingsSlice
> = (set) => ({
  meetings: [],
  hostsById: {},
  staffHostsById: {},
  start: new Date(),
  end: new Date(),
  isLoading: false,
  loadMeetings: async (start: Date, end: Date) => {
    const loadId = ++latestLoadId;
    set(() => ({ isLoading: true }));
    try {
      const [apiMeetings, apiHosts] = await Promise.all([
        MeetingResource.getList({
          start: start.toISOString(),
          end: end.toISOString(),
        }),
        HostResource.getList(),
      ]);
      const hosts = apiHosts.users.map(fromApiHost);
      const hostsById = hosts.reduce((acc, host) => {
        acc[host.id] = host;
        return acc;
      }, {} as Record<string, Host>);
      const staffHostsById = await loadStaffHosts(apiMeetings);
      // A Hosts-group member who is also staff keeps their Hosts-group name.
      const resolvable = { ...staffHostsById, ...hostsById };
      const meetingsRaw = apiMeetings.map((m) =>
        fromApiMeeting(m, resolvable),
      );
      const seen = new Set<string>();
      const meetings = meetingsRaw.filter((m) => {
        if (seen.has(m.id)) return false;
        seen.add(m.id);
        return true;
      });
      meetings.sort((a, b) => a.start.getTime() - b.start.getTime());
      if (loadId !== latestLoadId) return;
      set(() => ({ meetings, hostsById, staffHostsById, start, end }));
    } finally {
      // A superseded load leaves the spinner to the one still running.
      if (loadId === latestLoadId) set(() => ({ isLoading: false }));
    }
  },
  setInstanceMetadata: async (
    meetingId: string,
    metadata: InstanceMetadata
  ) => {
    await MeetingResource.update(meetingId, {
      instanceMetadata: {
        meetingType: metadata.meetingType,
        memberVisible: metadata.memberVisible,
        hostId: metadata.host ? metadata.host.id : undefined,
        continuousRoomKey: metadata.continuousRoomKey,
        partnerId: metadata.partnerId,
        useCalendarTitle: metadata.useCalendarTitle,
      },
    });
    // Optimistic update only — the backend list is cached per Lambda instance
    // (30 min TTL), so an immediate reload can return stale data and clobber
    // the values just saved. The next explicit load fetches server truth.
    set((state) => ({
      meetings: state.meetings.map((m) =>
        m.id === meetingId
          ? { ...m, host: metadata.host ?? m.host, metadata: { ...m.metadata, ...metadata } }
          : m
      ),
    }));
  },
  setRecurringMetadata: async (
    meetingId: string,
    metadata: RecurringMetadata
  ) => {
    await MeetingResource.update(meetingId, {
      recurringMetadata: {
        meetingType: metadata.meetingType,
        memberVisible: metadata.memberVisible,
        hostSchedule: metadata.hostSchedule
          ? toMeetingHostIdSchedule(metadata.hostSchedule)
          : undefined,
        continuousRoomKey: metadata.continuousRoomKey,
        partnerId: metadata.partnerId,
        useCalendarTitle: metadata.useCalendarTitle,
      },
    });
    // Optimistic update only — see setInstanceMetadata for why there is no
    // post-save reload.
    const scopeReachesFrom = Date.now() - SERIES_SCOPE_LOOKBACK_MS;
    set((state) => ({
      meetings: state.meetings.map((m) =>
        m.recurringMeeting?.id === meetingId
          ? {
              ...m,
              // A series scope and calendar-title flag reach every upcoming
              // occurrence (the backend rewrites the ones edited on their own
              // too). A private one also loses its room key, as on the backend.
              metadata:
                m.end.getTime() >= scopeReachesFrom
                  ? {
                      ...m.metadata,
                      ...(metadata.partnerId !== undefined && {
                        partnerId: metadata.partnerId,
                      }),
                      ...(metadata.partnerId?.trim() && {
                        continuousRoomKey: "",
                      }),
                      ...(metadata.useCalendarTitle !== undefined && {
                        useCalendarTitle: metadata.useCalendarTitle,
                      }),
                    }
                  : m.metadata,
              recurringMeeting: m.recurringMeeting
                ? {
                    ...m.recurringMeeting,
                    // Merged: the save sends only what the editor changes
                    // (partnerId only when it changed), so a replace would
                    // drop the rest. A private series loses its key, as on
                    // the backend.
                    metadata: {
                      ...m.recurringMeeting.metadata,
                      ...metadata,
                      ...(metadata.partnerId?.trim() && {
                        continuousRoomKey: "",
                      }),
                    },
                  }
                : m.recurringMeeting,
            }
          : m
      ),
    }));
  },
});
